// Wide top-anchored notification banner with an icon, message and dismiss
// (e.g. mini-cart's add-to-cart confirmation).
// For a small compact confirmation like "Link copied", use `./toast.ts`.

export type BannerVariant = 'positive' | 'critical';

export type ShowBannerOptions = {
  variant?: BannerVariant;
  dismissMs?: number;
  /** Extra class(es) for caller-specific overrides (e.g. `add-to-cart-banner` for the mini-cart's cart-link styling). Appended alongside the base `banner`/`banner--critical` classes. */
  className?: string;
};

const DEFAULT_DISMISS_MS = 3000;

function bannerIconMarkup(variant: BannerVariant): string {
  if (variant === 'critical') {
    return `<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
      <path d="M8 5v3.5M8 11h.01" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    </svg>`;
  }

  return `<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
    <path d="M5.5 8.25l1.75 1.75 3.25-3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;
}

function createBannerElement(
  messageHtml: string,
  variant: BannerVariant,
  className?: string
): HTMLElement {
  const banner = document.createElement('div');
  const classes = ['banner'];
  if (variant === 'critical') classes.push('banner--critical');
  if (className) classes.push(className);
  banner.className = classes.join(' ');
  banner.setAttribute('role', variant === 'critical' ? 'alert' : 'status');
  banner.setAttribute('aria-live', variant === 'critical' ? 'assertive' : 'polite');

  banner.innerHTML = `
    <div class="banner__body">
      <span class="banner__icon" aria-hidden="true">${bannerIconMarkup(variant)}</span>
      <p class="banner__message">${messageHtml}</p>
    </div>
    <button type="button" class="banner__close" aria-label="Dismiss notification">
      <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      </svg>
    </button>
  `;

  return banner;
}

/**
 * Shows a single transient banner. A second call replaces any banner already
 * showing (single-instance). `messageHtml` is trusted markup — callers MUST
 * escape any interpolated user/CMS content themselves (see mini-cart's `escapeHtml`).
 */
export function showBanner(messageHtml: string, options: ShowBannerOptions = {}): void {
  const { variant = 'positive', dismissMs = DEFAULT_DISMISS_MS, className } = options;

  const existing = document.querySelector('.banner');
  if (existing) existing.remove();

  const banner = createBannerElement(messageHtml, variant, className);
  document.body.appendChild(banner);

  let dismissTimeout: number | null = null;

  const dismiss = (): void => {
    banner.classList.add('banner--hiding');
    banner.addEventListener('animationend', () => banner.remove(), { once: true });
    // remove even if the hide animation never fires (reduced motion, missing CSS)
    window.setTimeout(() => banner.remove(), 400);
    if (dismissTimeout !== null) window.clearTimeout(dismissTimeout);
  };

  const closeBtn = banner.querySelector<HTMLButtonElement>('.banner__close');
  if (closeBtn) {
    closeBtn.addEventListener('click', dismiss);
  }

  requestAnimationFrame(() => {
    banner.classList.add('banner--visible');
  });

  dismissTimeout = window.setTimeout(dismiss, dismissMs);
}
